import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  CircularProgress,
  Divider,
  InputBase,
  Popover,
  Stack,
  Typography,
} from '@mui/material';

import { ROUTES } from '@/config/routes';
import { UserTypeContext } from '@/shared/lib/UserTypeContext/UserTypeContext';
import AppButton from '@/shared/ui/AppButton';

import NotificationsListHeader from '../NotificationsListHeader';
import NotificationDrawerCard from './NotificationDrawerCard';
import { Container } from './NotificationsSideMenu.styles';
import useNotificationsDrawerData from './useNotificationsDrawerData';
import useNotificationsSideMenuFilters, { getDateGroup } from './useNotificationsSideMenuFilters';

const PAGE_SIZE = 20;

interface Props {
  closeDrawer: () => void;
}

const NotificationsSideMenu: FC<Props> = ({ closeDrawer }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userType = useContext(UserTypeContext);

  const [searchQuery, setSearchQuery] = useState('');
  const { notifications, isLoading } = useNotificationsDrawerData();
  const hasOrdersAccess = userType !== 'service';

  const targetModule = useMemo(() => {
    if (!userType) return undefined;
    const moduleMap: Record<string, string> = {
      location: 'LOCATION',
      advertiser: 'ADVERTISER',
      backoffice: 'BACKOFFICE',
      service: 'SERVICES',
    };
    return moduleMap[userType];
  }, [userType]);

  const {
    activeSeverity,
    setActiveSeverity,
    timeFilter,
    setTimeFilter,
    timeAnchorEl,
    setTimeAnchorEl,
    timeOptions,
    chips,
    groupLabels,
    filteredNotifications,
    pendingOrdersCount,
    dateGroups: DATE_GROUPS,
  } = useNotificationsSideMenuFilters(
    notifications,
    hasOrdersAccess,
    searchQuery,
    setSearchQuery,
    targetModule,
  );

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [searchQuery, activeSeverity, timeFilter]);

  const hasMore = visibleCount < filteredNotifications.length;

  const visibleNotifications = useMemo(
    () => filteredNotifications.slice(0, visibleCount),
    [filteredNotifications, visibleCount],
  );

  const grouped = useMemo(() => {
    const groups: Record<string, typeof visibleNotifications> = {};
    for (const g of DATE_GROUPS) groups[g] = [];
    visibleNotifications.forEach((n) => {
      groups[getDateGroup(n.audit.timestamp)].push(n);
    });
    return groups;
  }, [visibleNotifications, DATE_GROUPS]);

  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;

  const totalRef = useRef(filteredNotifications.length);
  totalRef.current = filteredNotifications.length;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const scrollRoot = scrollRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current) {
          setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, totalRef.current));
        }
      },
      { root: scrollRoot, threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <Container data-testid="notifications-drawer">
      <NotificationsListHeader closeDrawer={closeDrawer} />

      <Stack direction="row" gap="0.5rem" sx={{ px: '1rem', pb: '0.5rem' }}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            display: 'flex',
            height: '1.8125rem',
            padding: '0.5rem 0.75rem',
            gap: '0.6875rem',
            flex: '1 0 0',
            borderRadius: '0.625rem',
            bgcolor: '#f5f5f7',
            transition: 'background-color 0.2s ease',
            '&:hover': { bgcolor: '#ebeaef' },
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            style={{ flexShrink: 0 }}
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M7.33333 1.5C10.555 1.5 13.1667 4.11167 13.1667 7.33333C13.1667 8.76413 12.6505 10.0737 11.7956 11.0885L14.3535 13.6465C14.5487 13.8418 14.5488 14.1583 14.3535 14.3535C14.1583 14.5488 13.8418 14.5487 13.6465 14.3535L11.0885 11.7956C10.0737 12.6505 8.76413 13.1667 7.33333 13.1667C4.11167 13.1667 1.5 10.555 1.5 7.33333C1.5 4.11167 4.11167 1.5 7.33333 1.5ZM7.33333 2.5C4.66396 2.5 2.5 4.66396 2.5 7.33333C2.5 10.0027 4.66396 12.1667 7.33333 12.1667C10.0027 12.1667 12.1667 10.0027 12.1667 7.33333C12.1667 4.66396 10.0027 2.5 7.33333 2.5Z"
              fill="#807D93"
            />
          </svg>
          <InputBase
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('notifications.search_placeholder')}
            sx={{
              color: '#615e6f',
              fontFamily: 'Neue Frutiger World',
              fontSize: '0.75rem',
              fontWeight: 400,
              lineHeight: 1.6,
              flex: 1,
              padding: '0 !important',
              border: 'none !important',
              borderRadius: 0,
              bgcolor: 'transparent',
              '& input': { bgcolor: 'transparent', padding: '0 !important' },
              '& input::placeholder': {
                color: '#615e6f',
                fontFamily: 'Neue Frutiger World',
                fontSize: '0.75rem',
                fontWeight: 400,
                lineHeight: 1.6,
                opacity: 1,
              },
            }}
          />
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          onClick={(e) => setTimeAnchorEl(e.currentTarget)}
          sx={{
            bgcolor: '#f5f5f7',
            borderRadius: '0.625rem',
            px: '0.75rem',
            height: '1.8125rem',
            width: '11.0625rem',
            flexShrink: 0,
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            '&:hover': { bgcolor: '#ebeaef' },
          }}
        >
          <Typography
            style={{
              color: '#615e6f',
              fontFamily: 'Neue Frutiger World',
              fontSize: '0.75rem',
              fontWeight: 400,
              lineHeight: 1.6,
              whiteSpace: 'nowrap',
            }}
          >
            {timeFilter === 'all'
              ? t('time_based', { defaultValue: 'Time Based' })
              : timeOptions.find((o) => o.key === timeFilter)?.label}
          </Typography>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M11.6464 5.64645C11.8417 5.45118 12.1582 5.45118 12.3535 5.64645C12.5487 5.84171 12.5487 6.15824 12.3535 6.35348L8.35348 10.3535C8.15824 10.5487 7.84171 10.5487 7.64645 10.3535L3.64645 6.35348C3.45118 6.15822 3.45118 5.84171 3.64645 5.64645C3.84171 5.45118 4.15822 5.45118 4.35348 5.64645L7.99996 9.29293L11.6464 5.64645Z"
              fill="#807D93"
            />
          </svg>
        </Stack>
      </Stack>

      <Popover
        open={Boolean(timeAnchorEl)}
        anchorEl={timeAnchorEl}
        onClose={() => setTimeAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableRestoreFocus
        PaperProps={{
          elevation: 0,
          sx: {
            mt: '0.25rem',
            borderRadius: '0.5rem',
            bgcolor: '#fff',
            boxShadow: '0px 1px 8px 0px rgba(0,0,0,0.15)',
            p: '0.5rem !important',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.125rem',
            minWidth: '11.0625rem',
          },
        }}
      >
        {timeOptions.map(({ key, label }) => (
          <Box
            key={key}
            onClick={() => {
              setTimeFilter(key);
              setTimeAnchorEl(null);
            }}
            sx={{
              px: '0.5rem',
              py: '0.25rem',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '0.625rem',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
              color: '#807D93',
              fontFamily: 'Neue Frutiger World',
              fontSize: '0.75rem',
              fontWeight: 400,
              lineHeight: 1.6,
              whiteSpace: 'nowrap',
              minHeight: 'unset',
            }}
          >
            {label}
          </Box>
        ))}
      </Popover>

      <Stack direction="row" justifyContent="space-between" sx={{ px: '1rem', py: '0.75rem' }}>
        {chips.map(({ key, label, color, icon }) => {
          const isActive = activeSeverity === key;
          return (
            <AppButton
              key={key}
              variant={key === 'critical' ? 's-alert' : 's-rounded'}
              size="small"
              startIcon={icon}
              onClick={() => setActiveSeverity(key)}
              sx={{
                borderRadius: '9999px',
                ...(color &&
                  key !== 'critical' && {
                    color: isActive ? '#fff' : color,
                    borderColor: color,
                    bgcolor: isActive ? color : 'transparent',
                    '&:hover': {
                      bgcolor: color,
                      borderColor: color,
                      color: '#fff',
                    },
                  }),
                ...(key === 'critical' &&
                  isActive && {
                    bgcolor: color,
                    color: '#fff',
                    '&:hover': { bgcolor: color },
                  }),
                ...(key === 'all' &&
                  isActive && {
                    bgcolor: 'accent.main',
                    borderColor: 'accent.main',
                    color: 'accent.contrastText',
                    '&:hover': { bgcolor: 'accent.dark', borderColor: 'accent.dark' },
                  }),
              }}
            >
              {label}
            </AppButton>
          );
        })}
      </Stack>

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          minHeight: 0,
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,0,0,0.2) transparent',
          '&::-webkit-scrollbar': { width: '0.25rem' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { borderRadius: '0.125rem', background: 'rgba(0,0,0,0.2)' },
        }}
      >
        {!isLoading && filteredNotifications.length === 0 && (
          <Stack
            data-testid="notifications-empty-state"
            alignItems="center"
            justifyContent="center"
            sx={{ pt: '3rem', gap: '0.75rem' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12.9626 20.9415C13.1573 20.6069 13.5865 20.4931 13.9211 20.6877C14.2558 20.8824 14.3697 21.3124 14.1749 21.6471C13.935 22.0593 13.5895 22.4003 13.1753 22.6376C12.7612 22.8748 12.2917 23 11.8142 23C11.3368 22.9999 10.8672 22.8748 10.4531 22.6376C10.0391 22.4003 9.69426 22.0592 9.45442 21.6471C9.25968 21.3124 9.37354 20.8824 9.7082 20.6877C10.0428 20.4932 10.4721 20.6069 10.6667 20.9415C10.7824 21.1401 10.949 21.3062 11.1506 21.4216C11.352 21.537 11.5809 21.5978 11.8142 21.5978C12.0477 21.5978 12.2772 21.5371 12.4788 21.4216C12.6803 21.3062 12.847 21.1402 12.9626 20.9415ZM5.037 8.23053C5.037 6.80679 5.49197 5.41995 6.33511 4.26954C7.17812 3.11938 8.36582 2.26435 9.72737 1.82668C11.0891 1.38905 12.5559 1.39136 13.9166 1.83216C15.2771 2.27298 16.463 3.13017 17.3034 4.28232C17.5315 4.59517 17.4628 5.03369 17.15 5.26184C16.8372 5.48995 16.3986 5.4213 16.1705 5.10848C15.5051 4.19626 14.5646 3.51597 13.4839 3.16587C12.4031 2.81584 11.238 2.81463 10.1564 3.16222C9.07512 3.50986 8.13356 4.18791 7.46617 5.09844C6.79873 6.00911 6.43918 7.10576 6.43918 8.23053C6.43916 10.5651 6.07027 12.1707 5.48888 13.3919C4.91261 14.6024 4.14903 15.3828 3.48145 16.0667C3.44132 16.1103 3.41514 16.1641 3.40568 16.221C3.39628 16.278 3.40392 16.3375 3.42759 16.3908C3.45131 16.444 3.49112 16.4895 3.5417 16.5222C3.59241 16.555 3.65183 16.5733 3.71333 16.5733H19.9169C19.9783 16.5733 20.0379 16.5558 20.0886 16.5231C20.1393 16.4904 20.1789 16.4441 20.2027 16.3908C20.2264 16.3375 20.2339 16.2788 20.2246 16.2219C20.2153 16.1651 20.1895 16.1113 20.1497 16.0676C19.6195 15.5246 19.0202 14.9126 18.5102 14.0757C18.309 13.7452 18.4136 13.3141 18.7439 13.1126C19.0745 12.9111 19.5064 13.0157 19.7079 13.3463C20.1282 14.0361 20.6242 14.5472 21.1648 15.1009L21.1676 15.1036L21.1822 15.1191C21.4062 15.3634 21.5549 15.6681 21.6085 15.9955C21.662 16.3228 21.6184 16.6585 21.4834 16.9613C21.3484 17.2642 21.1272 17.521 20.849 17.7007C20.6058 17.8577 20.3275 17.9503 20.0402 17.9709L19.916 17.9755H3.71241C3.38152 17.9753 3.05741 17.8797 2.77945 17.6998C2.50138 17.5198 2.28058 17.2626 2.14591 16.9595C2.01136 16.6565 1.96879 16.3209 2.02267 15.9937C2.07662 15.6665 2.22487 15.3621 2.44899 15.1182L2.46359 15.1027C3.13633 14.4135 3.75278 13.7764 4.22271 12.7894C4.69173 11.8042 5.03698 10.4169 5.037 8.23053ZM20.4354 5.72285C20.7103 5.4503 21.1542 5.45172 21.4268 5.7265C21.6994 6.00123 21.6977 6.44512 21.4232 6.71789L17.3718 10.7382C17.0985 11.0092 16.6574 11.0093 16.3841 10.7382L14.3593 8.72805C14.0845 8.45538 14.0822 8.01152 14.3548 7.73666C14.6275 7.46182 15.0713 7.46031 15.3461 7.73301L16.877 9.25204L20.4354 5.72285Z"
                fill="#807D93"
              />
            </svg>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#737085',
                fontFamily: 'Neue Frutiger World',
                fontWeight: 400,
              }}
            >
              {searchQuery.trim()
                ? t('no_notifications_matching_search', {
                    defaultValue: 'No notifications found matching your search criteria',
                  })
                : t('no_unread_notifications', { defaultValue: "You're all caught up!" })}
            </Typography>
          </Stack>
        )}

        {DATE_GROUPS.map((group) => {
          const items = grouped[group];
          if (!items?.length) return null;
          return (
            <Box key={group} sx={{ pb: '0.75rem' }}>
              <Box
                sx={{
                  bgcolor: '#fefefe',
                  px: '1rem',
                  pb: '0.5rem',
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                }}
              >
                <Divider sx={{ borderColor: '#d9d9d9', mb: '0.25rem' }} />
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: '#737085',
                    fontFamily: 'Neue Frutiger World',
                    fontWeight: 400,
                    lineHeight: 1.6,
                  }}
                >
                  {groupLabels[group]}
                </Typography>
              </Box>
              <Stack gap="0.5rem" sx={{ px: '1rem', pt: '0.5rem' }}>
                {items.map((n) => (
                  <NotificationDrawerCard
                    key={n.id}
                    notification={n}
                    pendingOrdersCount={pendingOrdersCount}
                    onNavigate={closeDrawer}
                  />
                ))}
              </Stack>
            </Box>
          );
        })}

        {hasMore && (
          <Stack alignItems="center" sx={{ py: '1rem' }}>
            <CircularProgress size={20} sx={{ color: '#807D93' }} />
          </Stack>
        )}

        <Box ref={sentinelRef} sx={{ height: '1px' }} />
      </Box>

      <Box sx={{ py: '0.625rem', textAlign: 'center' }}>
        <Divider sx={{ borderColor: '#d9d9d9', mx: '1rem', mb: '0.625rem' }} />
        <Typography
          component="span"
          onClick={() => {
            navigate(`${ROUTES.AUTHORIZED.CALENDAR}?view=day&t=${Date.now()}`);
            closeDrawer();
          }}
          sx={{
            fontSize: '0.75rem',
            color: '#737085',
            fontFamily: 'Neue Frutiger World',
            fontWeight: 400,
            lineHeight: 1.6,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {t('show_in_calendar', { defaultValue: 'Show in Calendar' })}
        </Typography>
      </Box>
    </Container>
  );
};

export default NotificationsSideMenu;
